"use client";

import { getOutsideSaleNote } from "@/utils/admin-settings";

import { createPaymentIntent, updatePaymentIntent } from "@/app/actions/payments";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { calculateTotal, formatCurrency, germanDayjs, hasValidTravelerName, requiresGuestRegistration, priceList, toRelativeDate } from "@/utils/helpers";
import { resolveCheckoutPaymentFeeCents } from "@/utils/checkout-payment-fees";
import { getEmailAutocompleteSuggestions } from "@/utils/email-provider-autocomplete";
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from "react";
import { FaCaravan, FaCarSide, FaVanShuttle, FaCircleCheck, FaShieldHalved, FaClock, FaDog, FaEnvelope, FaCar, FaUser, FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { Checkbox } from "../ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import CheckoutElement from "./checkout-element";
import InputNumber from "./input-number";
import PaymentSelect from "./payment-select";
import { useTicketSettings } from "@/hooks/use-ticket-settings";
import { captureClientEvent } from "@/lib/telemetry-client";

const PAYMENT_METHOD_ITEM_IDS = new Set(["paypal", "paypalpremium", "card", "apple_pay", "google_pay", "ideal", "klarna"]);

export default function Checkout({ ticket, hunde = 0, quantities = 1, onOpen }: { ticket: string, hunde?: number, quantities?: number, onOpen: boolean }) {
    const [step, setStep] = useState(1);
    const [intentId, setIntentId] = useState("");
    const [clientSecret, setClientSecret] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [kfz, setKfz] = useState<string>("");
    const [kfzCaravan, setKfzCaravan] = useState<string>("");
    const [paymentMethod, setPaymentMethod] = useState<string | undefined>(undefined);
    const [surferbowl, setSurferbowl] = useState(0);
    const [halbebroetchen, setHalbebroetchen] = useState(0);
    const [lachs, setLachs] = useState(0);
    const [hund, setHund] = useState(hunde);
    const [selectedTicket, setSelectedTicket] = useState(ticket || "");
    const [travelers, setTravelers] = useState<string[]>([]);
    const [total, setTotal] = useState(0);
    const [validTo, setValidTo] = useState<Dayjs | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [paymentError, setPaymentError] = useState<boolean>(false);
    const [quantity, setQuantity] = useState(quantities);
    const [nachzahlung, setNachzahlung] = useState(false);
    const [acceptedRules, setAcceptedRules] = useState(false);
    const [acceptedOffers, setAcceptedOffers] = useState(false);
    const prices = priceList();
    const { ticketHours, isTemporaryOverrideActive, checkoutPaymentFees } = useTicketSettings();
    const [checkoutSessionId] = useState(() => {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    });
    const previousStepRef = useRef<number>(1);
    const previousTicketRef = useRef<string>(ticket || "");
    const previousOnOpenRef = useRef(false);
    // Monotonic counter so only the most recent payment-intent request applies
    // its response. Without this, two updates racing on the same intent (e.g.
    // the stale value + its reset) could let the wrong amount win on Stripe.
    const requestSeqRef = useRef(0);

    const nights = selectedTicket === 'pkwnight' ? quantity : 1;
    // Travelers, Kurtaxe and AVS Meldeschein registration all apply only to an
    // actual overnight guest stay (womo/caravan) that is not a Nachzahlung.
    // PKW Abstellen (parking) and Nachzahlungen are exempt from all of these.
    const requiresRegistration = requiresGuestRegistration(selectedTicket, nachzahlung);
    const kurtaxeQuantity = requiresRegistration ? travelers.length * nights : 0;

    // Quantities/extras that actually apply to the selected ticket. Only pkwnight
    // is multi-night; dogs and food are only valid on womo/caravan. These are
    // derived (not read straight from state) so the displayed price and the
    // Stripe payload stay consistent even in the render right after the ticket
    // type changes, before the reset effect below has cleared the old state.
    // This is what prevents e.g. a leftover pkwnight quantity from being charged
    // as "womo x2".
    const supportsExtras = selectedTicket === 'womo' || selectedTicket === 'caravan';
    const ticketQuantity = selectedTicket === 'pkwnight' ? quantity : 1;
    const dogCount = supportsExtras ? hund : 0;
    const surferbowlCount = supportsExtras ? surferbowl : 0;
    const lachsCount = supportsExtras ? lachs : 0;
    const halbebroetchenCount = supportsExtras ? halbebroetchen : 0;
    // The caravan plate only belongs to a caravan ticket. Deriving it (instead of
    // sending the raw state) stops a leftover plate from a previous "caravan"
    // selection being stored on e.g. a womo booking after switching ticket type.
    const effectiveKfzCaravan = selectedTicket === 'caravan' ? kfzCaravan : "";
    const kurtaxeTotal = kurtaxeQuantity * (prices["kurtaxe" as keyof typeof prices] as number);
    const paymentFeeCents = resolveCheckoutPaymentFeeCents(checkoutPaymentFees, selectedTicket || null);

    const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
    const emailSuggestions = getEmailAutocompleteSuggestions(email);
    const isKfzValid = kfz.length >= 3;
    const isCaravanValid = selectedTicket !== 'caravan' || kfzCaravan.length >= 3;
    const isStep1Complete = Boolean(isEmailValid && isKfzValid && isCaravanValid && selectedTicket && acceptedRules);
    const areTravelersValid = travelers.length > 0 && travelers.every((name) => hasValidTravelerName(name));
    const isStep2Complete = !requiresRegistration || areTravelersValid;
    const isFormComplete = isStep1Complete && isStep2Complete && Boolean(paymentMethod) && acceptedRules;

    // Steps shown for the current ticket type (traveler step only for overnight)
    const visibleSteps = requiresRegistration ? [1, 2, 3] : [1, 3];

    const goNext = () => {
        if (step === 1) {
            const nextStep = requiresRegistration ? 2 : 3;
            captureClientEvent("checkout_step_advanced", {
                checkout_session_id: checkoutSessionId,
                from_step: 1,
                to_step: nextStep,
                ticket_type: selectedTicket,
            });
            setStep(nextStep);
            return;
        }
        if (step === 2) {
            captureClientEvent("checkout_step_advanced", {
                checkout_session_id: checkoutSessionId,
                from_step: 2,
                to_step: 3,
                ticket_type: selectedTicket,
            });
            setStep(3);
            return;
        }
    };
    const goBack = () => {
        if (step === 3) {
            const previousStep = requiresRegistration ? 2 : 1;
            captureClientEvent("checkout_step_returned", {
                checkout_session_id: checkoutSessionId,
                from_step: 3,
                to_step: previousStep,
                ticket_type: selectedTicket,
            });
            setStep(previousStep);
            return;
        }
        if (step === 2) {
            captureClientEvent("checkout_step_returned", {
                checkout_session_id: checkoutSessionId,
                from_step: 2,
                to_step: 1,
                ticket_type: selectedTicket,
            });
            setStep(1);
            return;
        }
    };

    const setTravelerCount = (count: number) => {
        captureClientEvent("checkout_traveler_count_changed", {
            checkout_session_id: checkoutSessionId,
            traveler_count: count,
            nights,
            ticket_type: selectedTicket,
            kurtaxe_total: kurtaxeTotal / 100,
        });
        setTravelers((prev) => {
            const next = [...prev];
            if (count > next.length) {
                while (next.length < count) next.push("");
            } else {
                next.length = count;
            }
            return next;
        });
    };

    useEffect(() => {
        const justOpened = onOpen && !previousOnOpenRef.current;
        if (justOpened) {
            setStep(1);
            setQuantity(quantities);
            setHund(hunde);
            setSelectedTicket(ticket);
            captureClientEvent("checkout_opened", {
                checkout_session_id: checkoutSessionId,
                initial_ticket: ticket,
                initial_hunde: hunde,
                initial_quantity: quantities,
            });
        }
        previousOnOpenRef.current = onOpen;
    }, [onOpen, quantities, hunde, ticket, checkoutSessionId]);

    useEffect(() => {
        const previousStep = previousStepRef.current;
        captureClientEvent("checkout_step_viewed", {
            checkout_session_id: checkoutSessionId,
            current_step: step,
            previous_step: previousStep,
            ticket_type: selectedTicket,
            is_overnight: requiresRegistration,
        });
        previousStepRef.current = step;
    }, [step, checkoutSessionId, selectedTicket, requiresRegistration]);

    useEffect(() => {
        const previousTicket = previousTicketRef.current;
        if (selectedTicket && selectedTicket !== previousTicket) {
            captureClientEvent("checkout_ticket_changed", {
                checkout_session_id: checkoutSessionId,
                previous_ticket: previousTicket || null,
                next_ticket: selectedTicket,
            });
        }
        previousTicketRef.current = selectedTicket;
    }, [selectedTicket, checkoutSessionId]);

    // Initialise / clear traveler names when registration applicability changes
    // (ticket type or Nachzahlung). Travelers are only collected for guest stays
    // that aren't a Nachzahlung.
    useEffect(() => {
        if (requiresGuestRegistration(selectedTicket, nachzahlung)) {
            setTravelers((prev) => (prev.length === 0 ? [""] : prev));
        } else {
            setTravelers([]);
        }
    }, [selectedTicket, nachzahlung]);

    useEffect(() => {
        if (selectedTicket !== 'womo' && selectedTicket !== 'caravan' && selectedTicket !== 'pkwnight' && nachzahlung) {
            setNachzahlung(false);
        }
    }, [selectedTicket, nachzahlung]);

    useEffect(() => {
        if ((selectedTicket === 'womo' || selectedTicket === 'caravan' || selectedTicket === 'pkwnight') && ticketHours && !isTemporaryOverrideActive) {
            const now = germanDayjs();
            const currentHour = now.hour();
            setNachzahlung(getOutsideSaleNote(selectedTicket, currentHour, ticketHours) === "Nachzahlung");
        } else {
            setNachzahlung(false);
        }
    }, [selectedTicket, ticketHours, isTemporaryOverrideActive]);

    // Clear extras / multi-night quantity from the inputs when switching to a
    // ticket that doesn't support them. Correctness no longer depends on this
    // (the payload is derived above) — it just keeps the UI tidy. Crucially this
    // is NOT done inside the payment effect anymore, so a reset can't race the
    // request that reads the value.
    useEffect(() => {
        if (selectedTicket !== 'womo' && selectedTicket !== 'caravan') {
            setHund(0);
            setSurferbowl(0);
            setLachs(0);
            setHalbebroetchen(0);
        }
        if (selectedTicket !== 'pkwnight') {
            setQuantity(1);
        }
    }, [selectedTicket]);

    useEffect(() => {
        async function initiatePayment(): Promise<void> {
            const items: { id: string; quantity: number }[] = [
                { id: selectedTicket, quantity: ticketQuantity },
                { id: "kurtaxe", quantity: kurtaxeQuantity },
                { id: "hund", quantity: dogCount },
                { id: "surferbowl", quantity: surferbowlCount },
                { id: "lachs", quantity: lachsCount },
                { id: "halbebroetchen", quantity: halbebroetchenCount },
                { id: paymentMethod || "", quantity: 1 },
            ];

            const filteredItems = items.filter((item) => item.quantity > 0);
            const subtotalWithoutPaymentMethod = calculateTotal(
                filteredItems.filter((item) => !PAYMENT_METHOD_ITEM_IDS.has(item.id))
            );
            const previewTotal = subtotalWithoutPaymentMethod + (paymentMethod ? paymentFeeCents : 0);
            setTotal(previewTotal);

            if (!paymentMethod || !email || !kfz || !selectedTicket) { return; }
            if (!kfzCaravan && selectedTicket === 'caravan') { return; }
            if (requiresRegistration && !areTravelersValid) { return; }

            const requestSeq = ++requestSeqRef.current;
            setIsLoading(true);
            setPaymentError(false);

            const cleanTravelers = travelers.map((name) => name.trim()).filter((name) => name.length > 0);

            let intentData;
            try {
                const paymentIntentAction = intentId ? "update" : "create";
                captureClientEvent("checkout_payment_intent_requested", {
                    checkout_session_id: checkoutSessionId,
                    action: paymentIntentAction,
                    payment_method: paymentMethod,
                    intent_id: intentId || null,
                    ticket_type: selectedTicket,
                    total: previewTotal / 100,
                    traveler_count: cleanTravelers.length,
                    kurtaxe_total: kurtaxeTotal / 100,
                    nights,
                });

                if (!intentId) {
                    intentData = await createPaymentIntent({
                        items: filteredItems,
                        paymentMethod: paymentMethod === 'paypalpremium' ? 'paypal' : paymentMethod,
                        kfz,
                        kfzCaravan: effectiveKfzCaravan,
                        recursive: nachzahlung,
                        travelers: cleanTravelers,
                        email,
                        marketingConsent: acceptedOffers
                    })
                } else {
                    intentData = await updatePaymentIntent({
                        id: intentId,
                        items: filteredItems,
                        paymentMethod: paymentMethod === 'paypalpremium' ? 'paypal' : paymentMethod,
                        kfz: kfz,
                        kfzCaravan: effectiveKfzCaravan,
                        recursive: nachzahlung,
                        travelers: cleanTravelers,
                        email,
                        marketingConsent: acceptedOffers
                    })
                }
                if (!intentData.id || !intentData.client_secret || !intentData.total) throw new Error("Payment intent error");
                // A newer request superseded this one while it was in flight —
                // ignore the stale response so the latest amount always wins.
                if (requestSeq !== requestSeqRef.current) { return; }
                setIntentId(intentData.id)
                setClientSecret(intentData.client_secret)
                setTotal(intentData.total);
                captureClientEvent("checkout_payment_intent_succeeded", {
                    checkout_session_id: checkoutSessionId,
                    action: paymentIntentAction,
                    payment_method: paymentMethod,
                    intent_id: intentData.id,
                    ticket_type: selectedTicket,
                    total: intentData.total / 100,
                    traveler_count: cleanTravelers.length,
                    kurtaxe_total: kurtaxeTotal / 100,
                });
            } catch (error) {
                if (requestSeq !== requestSeqRef.current) { return; }
                const message = error instanceof Error ? error.message : "unknown";
                captureClientEvent("checkout_payment_intent_failed", {
                    checkout_session_id: checkoutSessionId,
                    intent_id: intentId || null,
                    payment_method: paymentMethod,
                    ticket_type: selectedTicket,
                    error: message,
                });
                setIsLoading(false);
                setPaymentError(true);
                setPaymentMethod(undefined);
                return;
            }
            if (requestSeq === requestSeqRef.current) { setIsLoading(false); }
        }
        initiatePayment();
    }, [paymentMethod, paymentFeeCents, onOpen, ticketQuantity, email, dogCount, surferbowlCount, lachsCount, halbebroetchenCount, selectedTicket, nachzahlung, intentId, kfz, kfzCaravan, effectiveKfzCaravan, kurtaxeQuantity, travelers, requiresRegistration, areTravelersValid, checkoutSessionId, kurtaxeTotal, nights, acceptedOffers]);

    useEffect(() => {
        if (selectedTicket && ticketHours) {
            const now = dayjs.utc().tz('Europe/Berlin');
            switch (selectedTicket) {
                case 'pkwday': {
                    if (nachzahlung || now.hour() < ticketHours.dayTicketValidFrom) { setValidTo(now); break; }
                    setValidTo(now.hour(ticketHours.dayTicketValidUntil).minute(0).second(0));
                    break;
                }
                case 'four': {
                    if (nachzahlung || now.hour() < ticketHours.afternoonValidFrom) { setValidTo(now); break; }
                    if (now.hour() >= ticketHours.afternoonMaxUntil) {
                        setValidTo(now.hour(ticketHours.dayTicketValidUntil).minute(0).second(0));
                    } else {
                        setValidTo(now.add(4, 'hour'));
                    }
                    break;
                }
                case 'womo':
                case 'caravan':
                case 'pkwnight': {
                    if (nachzahlung) { setValidTo(now.hour(ticketHours.nightValidUntil).minute(0).second(0)); break; }
                    const saleStart = selectedTicket === 'womo' ? ticketHours.womoSaleStart
                        : selectedTicket === 'caravan' ? ticketHours.caravanSaleStart
                        : ticketHours.pkwNightSaleStart;
                    if (now.hour() >= saleStart) {
                        setValidTo(now.add(quantity, 'day').hour(ticketHours.nightValidUntil).minute(0).second(0));
                    } else {
                        setValidTo(now.add(quantity - 1, 'day').hour(ticketHours.nightValidUntil).minute(0).second(0));
                    }
                    break;
                }
                default:
                    setValidTo(undefined);
            }
        }
    }, [selectedTicket, quantity, nachzahlung, ticketHours]);

    const ticketPrice = selectedTicket ? (prices[selectedTicket as keyof typeof prices] as number * ticketQuantity) + (prices["hund" as keyof typeof prices] as number * dogCount) : 0;

    return (
        <SheetContent className="overflow-y-auto border-l-0 bg-gradient-to-b from-amber-50 to-amber-100 p-0">
            <SheetHeader className="sr-only">
                <SheetTitle>Ticket kaufen</SheetTitle>
                <SheetDescription>Kaufe dein Stellplatz-Ticket</SheetDescription>
            </SheetHeader>

            <div className="flex min-h-full flex-col">
                {/* Header */}
                <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 px-6 pb-8 pt-12">
                    <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-amber-400/10" />
                    <div className="absolute -left-8 bottom-0 h-24 w-24 rounded-full bg-amber-400/5" />

                    <div className="relative">
                        <p className="mb-1 font-handwritten text-lg text-amber-300">Fast geschafft!</p>
                        <h1 className="font-special text-3xl font-bold text-white">Ticket kaufen</h1>

                        {/* Step indicator */}
                        <div className="mt-4 flex items-center gap-2">
                            {visibleSteps.map((s, idx) => (
                                <div key={s} className="flex items-center gap-2">
                                    <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors ${step === s ? 'bg-amber-400 text-slate-900' : step > s ? 'bg-amber-400/40 text-white' : 'bg-white/15 text-white/70'}`}>
                                        {idx + 1}
                                    </div>
                                    {idx < visibleSteps.length - 1 && (
                                        <div className={`h-0.5 w-6 rounded-full ${step > s ? 'bg-amber-400/40' : 'bg-white/15'}`} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Wave divider */}
                    <div className="absolute -bottom-1 left-0 right-0">
                        <svg viewBox="0 0 400 30" fill="none" className="w-full" preserveAspectRatio="none">
                            <path d="M0 30L16.7 25C33.3 20 66.7 10 100 7.5C133.3 5 166.7 10 200 12.5C233.3 15 266.7 15 300 12.5C333.3 10 366.7 5 383.3 2.5L400 0V30H383.3C366.7 30 333.3 30 300 30C266.7 30 233.3 30 200 30C166.7 30 133.3 30 100 30C66.7 30 33.3 30 16.7 30H0Z" className="fill-amber-50" />
                        </svg>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-6 px-6 py-6">

                    {/* Step 1: Ticket + Data */}
                    {step === 1 && (
                        <>
                            <section className="rounded-2xl bg-white p-5 shadow-lg shadow-amber-200/50">
                                <div className="mb-4 flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-white">1</div>
                                    <h2 className="font-special text-lg font-bold text-slate-800">Ticket auswählen</h2>
                                </div>

                                <Select defaultValue={selectedTicket} onValueChange={(value) => setSelectedTicket(value)}>
                                    <SelectTrigger className={`h-14 rounded-xl border-2 text-base font-semibold transition-all ${selectedTicket ? 'border-amber-400 bg-amber-50' : 'border-slate-200'}`}>
                                        <SelectValue placeholder="Bitte wählen..." />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl">
                                        <SelectItem value="pkwday" disabled={germanDayjs().hour() < 7 || germanDayjs().hour() > 22}>
                                            <div className="flex items-center gap-3 py-1">
                                                <FaCarSide className="h-5 w-5 text-slate-600" />
                                                <span>Tagesticket</span>
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="four" disabled={germanDayjs().hour() < 14 || germanDayjs().hour() > 22}>
                                            <div className="flex items-center gap-3 py-1">
                                                <FaCarSide className="h-5 w-5 text-slate-600" />
                                                <span>4 Stunden (ab 14 Uhr)</span>
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="womo">
                                            <div className="flex items-center gap-3 py-1">
                                                <FaVanShuttle className="h-5 w-5 text-slate-600" />
                                                <span>Übernachtung (bis {ticketHours?.nightValidUntil ?? 9} Uhr)</span>
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="caravan">
                                            <div className="flex items-center gap-3 py-1">
                                                <FaCaravan className="h-5 w-5 text-slate-600" />
                                                <span>PKW + Caravan (bis {ticketHours?.nightValidUntil ?? 9} Uhr)</span>
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="pkwnight">
                                            <div className="flex items-center gap-3 py-1">
                                                <FaCarSide className="h-5 w-5 text-slate-600" />
                                                <span>PKW Abstellen</span>
                                            </div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>

                                {/* Extras */}
                                {(selectedTicket === 'womo' || selectedTicket === 'caravan') && (
                                    <div className="mt-4">
                                        <InputNumber
                                            label="Hund(e)"
                                            value={hund}
                                            setValue={setHund}
                                            price={prices["hund" as keyof typeof prices] as number}
                                            icon={<FaDog className="h-6 w-6 text-amber-600" />}
                                        />
                                    </div>
                                )}

                                {selectedTicket === 'pkwnight' && (
                                    <div className="mt-4">
                                        <InputNumber
                                            label="Übernachtungen"
                                            value={quantity}
                                            setValue={setQuantity}
                                            price={prices["pkwnight" as keyof typeof prices] as number}
                                            min={1}
                                            max={14}
                                            icon={<FaClock className="h-6 w-6 text-amber-600" />}
                                        />
                                    </div>
                                )}

                                {/* Nachzahlung option (WOMO / Caravan / PKW Abstellen) */}
                                {(selectedTicket === 'womo' || selectedTicket === 'caravan' || selectedTicket === 'pkwnight') && (
                                    <div className="mt-4 flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                                        {(() => {
                                            const now = germanDayjs();
                                            const currentHour = now.hour();
                                            const outsideWindow = ticketHours && getOutsideSaleNote(selectedTicket, currentHour, ticketHours) === "Nachzahlung";
                                            const isForcedNachzahlung = Boolean(outsideWindow && !isTemporaryOverrideActive);
                                            return (
                                                <Checkbox
                                                    checked={nachzahlung}
                                                    onCheckedChange={() => !isForcedNachzahlung && setNachzahlung(!nachzahlung)}
                                                    className="h-5 w-5 border-2 data-[state=checked]:border-amber-500 data-[state=checked]:bg-amber-500"
                                                />
                                            );
                                        })()}
                                        <div className="flex-1">
                                            <p className={`text-sm font-semibold ${nachzahlung ? 'text-slate-800' : 'text-slate-500'}`}>
                                                {selectedTicket === 'pkwnight'
                                                    ? 'Nachzahlung (letzte Nacht)'
                                                    : 'Nachzahlung (letzte Nacht)'}
                                            </p>
                                        </div>
                                        <Popover>
                                            <PopoverTrigger className="text-slate-400 hover:text-slate-600">
                                                <span className="text-xs underline">Info</span>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-64 rounded-xl text-sm">
                                                    Die Nachzahlung gilt nur für die vergangene Nacht.
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                )}

                                {/* Price display */}
                                {selectedTicket && (
                                    <div className="mt-4 flex items-end justify-between rounded-xl bg-gradient-to-r from-amber-100 to-amber-50 p-4">
                                        <div>
                                            <p className="text-xs font-medium text-slate-500">Ticketpreis</p>
                                            <p className="font-special text-3xl font-bold text-slate-800">
                                                {formatCurrency(ticketPrice)}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs font-medium text-slate-500">Gültig bis</p>
                                            <p className={`text-sm font-semibold ${nachzahlung ? 'text-red-600' : 'text-emerald-600'}`}>
                                                {validTo ? toRelativeDate(validTo, 'full', 2) : "—"}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </section>

                            {/* Contact Info */}
                            <section className="rounded-2xl bg-white p-5 shadow-lg shadow-amber-200/50">
                                <div className="mb-4 flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-white">2</div>
                                    <h2 className="font-special text-lg font-bold text-slate-800">Deine Daten</h2>
                                </div>

                                <div className="space-y-3">
                                    <div className="relative">
                                        <FaEnvelope className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                        <Input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="E-Mail Adresse"
                                            autoComplete="email"
                                            list="checkout-email-provider-suggestions"
                                            className={`h-12 pl-9! rounded-xl border-2 text-base transition-all ${isEmailValid ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}
                                        />
                                        <datalist id="checkout-email-provider-suggestions">
                                            {emailSuggestions.map((suggestion) => (
                                                <option key={suggestion} value={suggestion} />
                                            ))}
                                        </datalist>
                                        {isEmailValid && <FaCircleCheck className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />}
                                    </div>

                                    <div className="relative">
                                        <FaCar className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                        <Input
                                            type="text"
                                            value={kfz}
                                            onChange={(e) => setKfz(e.target.value.toUpperCase())}
                                            placeholder="KFZ-Kennzeichen"
                                            className={`h-12 pl-9! rounded-xl border-2 text-base uppercase transition-all ${isKfzValid ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}
                                        />
                                        {isKfzValid && <FaCircleCheck className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />}
                                    </div>

                                    {selectedTicket === 'caravan' && (
                                        <div className="relative">
                                            <FaCaravan className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                            <Input
                                                type="text"
                                                value={kfzCaravan}
                                                onChange={(e) => setKfzCaravan(e.target.value.toUpperCase())}
                                                placeholder="Caravan-Kennzeichen"
                                                className={`h-12 rounded-xl border-2 pl-9! text-base uppercase transition-all ${kfzCaravan.length >= 3 ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}
                                            />
                                            {kfzCaravan.length >= 3 && <FaCircleCheck className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 space-y-3">
                                    <label
                                        htmlFor="accept-rules"
                                        className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3 transition-all ${acceptedRules ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}
                                    >
                                        <Checkbox
                                            id="accept-rules"
                                            checked={acceptedRules}
                                            onCheckedChange={(checked) => {
                                                const nextValue = checked === true;
                                                setAcceptedRules(nextValue);
                                                captureClientEvent("checkout_rules_toggled", {
                                                    checkout_session_id: checkoutSessionId,
                                                    accepted_rules: nextValue,
                                                    ticket_type: selectedTicket,
                                                });
                                            }}
                                            className="mt-0.5 h-5 w-5 border-2 data-[state=checked]:border-emerald-500 data-[state=checked]:bg-emerald-500"
                                        />
                                        <span className="text-xs text-slate-700">
                                            Ich habe die{" "}
                                            <a
                                                href="/#regeln"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-semibold text-amber-700 underline"
                                            >
                                                Platzregeln
                                            </a>
                                            {" "}gelesen und akzeptiere diese.
                                        </span>
                                    </label>

                                    <label
                                        htmlFor="accept-offers"
                                        className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3 transition-all ${acceptedOffers ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}
                                    >
                                        <Checkbox
                                            id="accept-offers"
                                            checked={acceptedOffers}
                                            onCheckedChange={(checked) => {
                                                const nextValue = checked === true;
                                                setAcceptedOffers(nextValue);
                                                captureClientEvent("checkout_offers_consent_toggled", {
                                                    checkout_session_id: checkoutSessionId,
                                                    accepted_offers: nextValue,
                                                    ticket_type: selectedTicket,
                                                });
                                            }}
                                            className="mt-0.5 h-5 w-5 border-2 data-[state=checked]:border-amber-500 data-[state=checked]:bg-amber-500"
                                        />
                                        <span className="text-xs text-slate-700">
                                            Ja, ich m&ouml;chte per E-Mail Infos zu Angeboten und Aktionen vom Stellplatz Zingst erhalten. Abmeldung jederzeit m&ouml;glich.
                                        </span>
                                    </label>
                                </div>
                            </section>

                            {/* Nav */}
                            <button
                                type="button"
                                disabled={!isStep1Complete}
                                onClick={goNext}
                                className={`flex w-full items-center justify-center gap-2 rounded-full py-4 font-semibold transition-all ${isStep1Complete ? 'bg-slate-800 text-white hover:bg-slate-900' : 'cursor-not-allowed bg-slate-300 text-slate-500'}`}
                            >
                                Weiter <FaArrowRight className="h-4 w-4" />
                            </button>
                        </>
                    )}

                    {/* Step 2: Traveler names (overnight only) */}
                    {step === 2 && requiresRegistration && (
                        <>
                            <section className="rounded-2xl bg-white p-5 shadow-lg shadow-amber-200/50">
                                <div className="mb-4 flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-white">2</div>
                                    <h2 className="font-special text-lg font-bold text-slate-800">Reisende (Kurtaxe)</h2>
                                </div>

                                <p className="mb-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                                    Bitte gib die Namen aller Übernachtungsgäste ab 15 Jahren an. Für jede Person wird die Kurtaxe von {formatCurrency(prices["kurtaxe" as keyof typeof prices] as number)} pro Nacht erhoben.
                                </p>

                                <div className="mb-4">
                                    <InputNumber
                                        label="Anzahl Reisende (ab 15 J.)"
                                        value={travelers.length}
                                        setValue={setTravelerCount}
                                        price={prices["kurtaxe" as keyof typeof prices] as number}
                                        min={1}
                                        max={10}
                                        icon={<FaUser className="h-5 w-5 text-amber-600" />}
                                    />
                                </div>

                                <div className="space-y-3">
                                    {travelers.map((name, index) => (
                                        <div key={index} className="relative">
                                            <FaUser className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                            <Input
                                                type="text"
                                                value={name}
                                                onChange={(e) => {
                                                    const next = [...travelers];
                                                    next[index] = e.target.value;
                                                    setTravelers(next);
                                                }}
                                                placeholder={`Vor- und Nachname Reisende/r ${index + 1}`}
                                                className={`h-12 pl-9! rounded-xl border-2 text-base transition-all ${hasValidTravelerName(name) ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}
                                            />
                                            {hasValidTravelerName(name) && <FaCircleCheck className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />}
                                        </div>
                                    ))}
                                </div>
                                {!areTravelersValid && (
                                    <p className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                                        Bitte pro Reisende/r einen Namen eingeben, keine E-Mailadresse (Vor- und Nachname sind empfohlen).
                                    </p>
                                )}

                                {/* Kurtaxe subtotal */}
                                <div className="mt-4 flex items-end justify-between rounded-xl bg-gradient-to-r from-amber-100 to-amber-50 p-4">
                                    <div>
                                        <p className="text-xs font-medium text-slate-500">Kurtaxe</p>
                                        <p className="text-xs text-slate-500">{travelers.length} {travelers.length === 1 ? 'Person' : 'Personen'} × {nights} {nights === 1 ? 'Nacht' : 'Nächte'}</p>
                                    </div>
                                    <p className="font-special text-2xl font-bold text-slate-800">
                                        {formatCurrency(kurtaxeTotal)}
                                    </p>
                                </div>
                            </section>

                            {/* Nav */}
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={goBack}
                                    className="flex items-center justify-center gap-2 rounded-full border-2 border-slate-300 bg-white px-6 py-4 font-semibold text-slate-700 transition-all hover:bg-slate-50"
                                >
                                    <FaArrowLeft className="h-4 w-4" /> Zurück
                                </button>
                                <button
                                    type="button"
                                    disabled={!isStep2Complete}
                                    onClick={goNext}
                                    className={`flex flex-1 items-center justify-center gap-2 rounded-full py-4 font-semibold transition-all ${isStep2Complete ? 'bg-slate-800 text-white hover:bg-slate-900' : 'cursor-not-allowed bg-slate-300 text-slate-500'}`}
                                >
                                    Weiter <FaArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </>
                    )}

                    {/* Step 3: Payment */}
                    {step === 3 && (
                        <>
                            <section className="rounded-2xl bg-white p-5 shadow-lg shadow-amber-200/50">
                                <div className="mb-4 flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-white">{requiresRegistration ? 3 : 2}</div>
                                    <h2 className="font-special text-lg font-bold text-slate-800">Zahlungsmethode</h2>
                                </div>

                                <PaymentSelect
                                    paymentMethod={paymentMethod}
                                    selectPaymentMethodAction={(value) => {
                                        setPaymentMethod(value);
                                        captureClientEvent("payment_method_selected", {
                                            checkout_session_id: checkoutSessionId,
                                            payment_method: value,
                                            ticket_type: selectedTicket,
                                            traveler_count: travelers.length,
                                            kurtaxe_total: kurtaxeTotal / 100,
                                        });
                                    }}
                                    total={total}
                                    surchargeHint={paymentFeeCents > 0 ? `zzgl. ${formatCurrency(paymentFeeCents)} Transaktionsgebühr` : "Keine Transaktionsgebühr"}
                                />
                            </section>

                            {/* Complete Payment */}
                            <section className="rounded-2xl bg-white p-5 shadow-lg shadow-amber-200/50">
                                <div className="mb-4 flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-white">{requiresRegistration ? 4 : 3}</div>
                                    <h2 className="font-special text-lg font-bold text-slate-800">Bezahlung abschließen</h2>
                                </div>

                                {/* Summary */}
                                <div className="mb-4 rounded-xl bg-slate-50 p-4">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-slate-600">Gültig bis:</span>
                                        <span className="font-semibold text-slate-800">{validTo ? toRelativeDate(validTo, 'full', 2) : "—"}</span>
                                    </div>
                                    {kurtaxeTotal > 0 && (
                                        <div className="mt-2 flex items-center justify-between text-sm">
                                            <span className="text-slate-600">davon Kurtaxe:</span>
                                            <span className="font-semibold text-slate-800">{formatCurrency(kurtaxeTotal)}</span>
                                        </div>
                                    )}
                                    <div className="mt-2 flex items-center justify-between">
                                        <span className="text-slate-600">Gesamtkosten:</span>
                                        <span className="font-special text-xl font-bold text-slate-800">{formatCurrency(total)}</span>
                                    </div>
                                </div>

                                {/* Notice */}
                                <div className="mb-4 rounded-xl border-2 border-amber-400/50 bg-amber-50 p-4">
                                    <p className="text-sm font-semibold text-slate-800">
                                        Keine Reservierung: Bitte erst Platz finden, dann bezahlen.
                                    </p>
                                    <p className="mt-1 text-xs text-amber-800">
                                        Eine Stornierung ist nicht möglich.
                                    </p>
                                </div>

                                {/* Payment form */}
                                <div className={`relative transition-opacity ${isFormComplete ? 'opacity-100' : 'opacity-50'}`}>
                                    {!isFormComplete && (
                                        <div className="absolute inset-0 z-10 cursor-not-allowed rounded-xl" />
                                    )}
                                    <CheckoutElement
                                        clientSecret={clientSecret}
                                        paymentMethod={paymentMethod}
                                        email={email}
                                        total={total}
                                        isLoading={isLoading}
                                        paymentError={paymentError}
                                        kfz={kfz}
                                        caravan={selectedTicket === 'caravan' ? kfzCaravan : ""}
                                        selectedTicket={selectedTicket}
                                    />
                                </div>
                            </section>

                            {/* Back nav */}
                            <button
                                type="button"
                                onClick={goBack}
                                className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-300 bg-white py-4 font-semibold text-slate-700 transition-all hover:bg-slate-50"
                            >
                                <FaArrowLeft className="h-4 w-4" /> Zurück
                            </button>
                        </>
                    )}

                    {/* Trust footer */}
                    <div className="flex items-center justify-center gap-2 pb-8 pt-4 text-slate-400">
                        <FaShieldHalved className="h-4 w-4" />
                        <span className="text-xs">Sichere Bezahlung über Stripe</span>
                    </div>
                </div>
            </div>
        </SheetContent>
    )
}
